
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { toast } from "sonner";
import { uploadImage } from "@/utils/storage";

interface ChatMessage {
  id: string;
  company_id: string;
  user_id: string | null;
  user_name: string;
  email: string | null;
  content: string;
  is_admin: boolean;
  created_at: string;
  image_url?: string;
}

interface UseChatWidgetProps {
  companyId: string;
  companyName: string;
  userName?: string;
  userId?: string;
  embedded?: boolean;
  userEmail?: string;
}

const activeUsers = new Set<string>();

export const useChatWidget = ({
  companyId,
  companyName,
  userName,
  userId,
  embedded = false,
  userEmail
}: UseChatWidgetProps) => {
  const [isOpen, setIsOpen] = useState(embedded ? true : false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [visitorName, setVisitorName] = useState(userName || "");
  const [visitorEmail, setVisitorEmail] = useState(userEmail || "");
  const [nameSubmitted, setNameSubmitted] = useState(!!userName);
  const [activeTab, setActiveTab] = useState<string>("home");
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [aiSupportEnabled, setAiSupportEnabled] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [changelogs, setChangelogs] = useState<any[]>([]);
  const [isLoadingChangelogs, setIsLoadingChangelogs] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast: uiToast } = useToast();
  const channelRef = useRef<any>(null);
  const processingMessageRef = useRef<string | null>(null);

  useEffect(() => {
    if (userId && isOpen) {
      activeUsers.add(userId);
      return () => {
        activeUsers.delete(userId);
      };
    }
  }, [userId, isOpen]);

  useEffect(() => {
    const checkAISupport = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('ai_support_enabled')
          .eq('id', companyId)
          .single();
          
        if (error) {
          console.error('Error checking AI support:', error);
          return;
        }
        
        setAiSupportEnabled(data?.ai_support_enabled || false);
      } catch (err) {
        console.error('Error in checkAISupport:', err);
      }
    };
    
    if (companyId) {
      checkAISupport();
    }
  }, [companyId]);

  useEffect(() => {
    if (isOpen && nameSubmitted && visitorEmail && activeTab === "chat") {
      fetchMessages();
      setupRealtimeSubscription();
    }
    
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isOpen, nameSubmitted, visitorEmail, companyId, activeTab]);

  useEffect(() => {
    if (activeTab === "chat") {
      scrollToBottom();
    }
    if (activeTab === "changelog" && !changelogs.length) {
      fetchChangelogs();
    }
  }, [messages, activeTab, changelogs.length]);

  const fetchChangelogs = async () => {
    setIsLoadingChangelogs(true);
    try {
      const { data, error } = await supabase
        .from('changelogs')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(5);
        
      if (error) {
        throw error;
      }
      
      setChangelogs(data || []);
    } catch (error) {
      console.error('Error fetching changelogs:', error);
    } finally {
      setIsLoadingChangelogs(false);
    }
  };

  const fetchMessages = async () => {
    if (!visitorEmail) return;
    setIsLoading(true);
    try {
      console.log(`Fetching messages for visitor email: ${visitorEmail} in company: ${companyId}`);
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("company_id", companyId)
        .eq("email", visitorEmail)
        .order("created_at", { ascending: true });
        
      if (error) throw error;
      
      console.log(`Found ${data?.length || 0} messages for ${visitorEmail}`);
      setMessages(data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
      uiToast({
        title: "Error",
        description: "Failed to load chat messages",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!visitorName) return;
    
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }
    
    console.log(`Setting up real-time subscription for visitor: ${visitorName}`);
    const channel = supabase
      .channel("public:chat_messages")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "chat_messages",
        filter: `company_id=eq.${companyId}`
      }, payload => {
        console.log("Chat event received in widget:", payload);
        if (payload.eventType === "INSERT") {
          const newMessage = payload.new as ChatMessage;
          console.log("Checking message relevance:", newMessage);
          console.log("Current visitor name:", visitorName);

          // Only add admin messages if they weren't generated by our local UI
          if (newMessage.user_name === visitorName) {
            // For admin messages (AI responses), only add if we're not currently showing the local version
            if (newMessage.is_admin) {
              // Check if this is a message we're already processing locally
              if (processingMessageRef.current === newMessage.content) {
                console.log("Ignoring real-time admin message that matches our local state:", newMessage.content.substring(0, 30));
                return;
              }
            }
            
            console.log("Adding new message to chat widget:", newMessage);
            setMessages(prev => {
              if (prev.some(msg => msg.id === newMessage.id)) {
                return prev;
              }
              return [...prev, newMessage];
            });
            
            setTimeout(scrollToBottom, 100);
          }
        }
        
        if (payload.eventType === "DELETE" && payload.old) {
          const deletedId = (payload.old as any).id;
          if (deletedId) {
            setMessages(prev => prev.filter(msg => msg.id !== deletedId));
          }
        }
      });

    channel.subscribe(status => {
      console.log(`Chat widget subscription status for ${visitorName}:`, status);
      if (status === "SUBSCRIBED") {
        console.log("Successfully subscribed to chat messages in widget");
      } else if (status === "CHANNEL_ERROR") {
        console.error("Error subscribing to chat messages in widget");
        setTimeout(() => {
          if (channelRef.current) {
            console.log("Attempting to resubscribe...");
            channelRef.current.subscribe();
          }
        }, 2000);
      }
    });
    
    channelRef.current = channel;
    console.log("Chat widget real-time subscription set up with channel:", channel);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth"
    });
  };

  const handleSubmitName = (e: React.FormEvent) => {
    e.preventDefault();
    if (visitorName.trim() && visitorEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(visitorEmail)) {
        toast("Please enter a valid email address");
        return;
      }
      setNameSubmitted(true);
    } else {
      toast("Please enter both your name and email");
    }
  };

  const sendMessage = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!message.trim() && !selectedImage || !visitorName || !visitorEmail) return;
    
    try {
      setIsUploading(selectedImage !== null);
      let imageUrl: string | undefined;
      
      if (selectedImage) {
        try {
          imageUrl = await uploadImage(selectedImage, 'chat-images');
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          toast("Failed to upload image. Please try again.");
          setIsUploading(false);
          return;
        }
      }
      
      const newMessage = {
        company_id: companyId,
        user_id: userId || null,
        user_name: visitorName,
        email: visitorEmail,
        content: message.trim() || (imageUrl ? 'Sent an image' : ''),
        is_admin: false,
        image_url: imageUrl
      };
      
      const { error } = await supabase.from("chat_messages").insert(newMessage);
      if (error) throw error;
      
      const messageContent = message;
      setMessage("");
      setSelectedImage(null);
      setIsUploading(false);
      
      if (aiSupportEnabled) {
        setIsProcessingAI(true);
        try {
          const { data: aiResponse, error: aiError } = await supabase.functions.invoke('ai-chat-response', {
            body: {
              message: messageContent,
              companyId,
              userName: visitorName
            }
          });
          
          if (aiError) {
            console.error('Error getting AI response:', aiError);
          } else {
            console.log('AI response received:', aiResponse);
            const responseText = typeof aiResponse === 'string' ? aiResponse : "I'm sorry, I couldn't generate a response at this time.";

            // Store the AI response we're about to add locally so we can filter duplicates
            processingMessageRef.current = responseText;

            // Add temp message that will be replaced with real one from database once inserted
            const tempAiMessage = {
              id: `ai-temp-${Date.now()}`,
              company_id: companyId,
              user_id: null,
              user_name: visitorName,
              content: responseText,
              is_admin: true,
              created_at: new Date().toISOString()
            };

            // We no longer need to add the message to state here since the backend
            // will insert it into the database and we'll get it via the real-time subscription
            // This prevents duplicate messages
          }
        } catch (aiErr) {
          console.error('Error invoking AI chat response:', aiErr);
        } finally {
          setIsProcessingAI(false);
          // Clear the processing message reference after a delay
          setTimeout(() => {
            processingMessageRef.current = null;
          }, 2000);
        }
      } else {
        try {
          const activeUserIds = Array.from(activeUsers);
          await supabase.functions.invoke('send-chat-notification', {
            body: {
              companyId,
              messageContent,
              userName: visitorName,
              activeUserIds,
              isAdminMessage: false
            }
          });
          console.log('Chat notification sent to offline team members');
        } catch (notifyError) {
          console.error('Error sending chat notification:', notifyError);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setIsUploading(false);
      uiToast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    }
  };

  const handleImageSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast("Please select an image file.");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast("Image must be less than 5MB.");
        return;
      }
      
      setSelectedImage(file);
    }
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // Navigate to specific tab from home screen
  const navigateToTab = (tabName: string) => {
    setActiveTab(tabName);
  };

  // Helper function to lighten/darken color
  const adjustColor = (color: string, amount: number) => {
    let usePound = false;
    if (color[0] === "#") {
      color = color.slice(1);
      usePound = true;
    }
    
    const num = parseInt(color, 16);
    let r = (num >> 16) + amount;
    r = Math.min(255, Math.max(0, r));
    let g = (num >> 8 & 0x00FF) + amount;
    g = Math.min(255, Math.max(0, g));
    let b = (num & 0x0000FF) + amount;
    b = Math.min(255, Math.max(0, b));
    
    return (usePound ? "#" : "") + (g | r << 8 | b << 16).toString(16).padStart(6, '0');
  };

  const getGradientStyle = () => {
    if (!companyId) return {};
    return {
      background: `linear-gradient(135deg, ${companyId} 0%, ${adjustColor(companyId, 30)} 100%)`
    };
  };

  return {
    isOpen,
    setIsOpen,
    message,
    setMessage,
    messages,
    isLoading,
    visitorName,
    setVisitorName,
    visitorEmail,
    setVisitorEmail,
    nameSubmitted,
    setNameSubmitted,
    activeTab,
    setActiveTab,
    isProcessingAI,
    aiSupportEnabled,
    isUploading,
    selectedImage,
    setSelectedImage,
    changelogs,
    isLoadingChangelogs,
    fileInputRef,
    messagesEndRef,
    handleSubmitName,
    sendMessage,
    handleImageSelect,
    handleFileChange,
    removeSelectedImage,
    formatTime,
    handleTabChange,
    navigateToTab,
    adjustColor,
    getGradientStyle,
    fetchChangelogs,
    scrollToBottom
  };
};
